/**
 * LayoutModal Component
 * =====================
 * Modal popup for selecting page layouts with large previews and descriptions.
 * 
 * Features:
 * - Large layout preview images
 * - Detailed descriptions
 * - Visual indicators for current layout
 * - Slot information
 * - Responsive grid display
 * - Smooth animations
 * 
 * @component
 */

import React, { useState } from 'react';
import { getAvailableLayouts } from '../../config/layouts';

// Visual layout preview component - renders a visual representation of the layout structure
const LayoutPreviewVisual = ({ layout }) => {
  const slots = layout.slots || [];
  const hasHeader = slots.some(s => s.id === 'header');
  const hasFooter = slots.some(s => s.id === 'footer');
  const contentSlots = slots.filter(s => s.id !== 'header' && s.id !== 'footer');
  
  // Determine layout structure based on layout ID and slot configuration
  const isSingleColumn = layout.id === 'single-column';
  const isTwoColumn = layout.id === 'two-column' || layout.id === 'two-column-split';
  const isThreeColumn = layout.id === 'three-column' || layout.id === 'three-equal-columns';
  const isFourColumn = layout.id === 'four-column' || layout.id === 'four-equal-columns';
  const isSidebarLeft = layout.id === 'sidebar-left-main' || layout.id === 'left-sidebar-main';
  const isSidebarRight = layout.id === 'sidebar-right-main' || layout.id === 'main-right-sidebar';
  const isHero = layout.id === 'hero-content' || layout.id === 'hero-plus-grid';
  
  // Get column count from content slots
  const columnCount = contentSlots.length;
  
  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      padding: '12px',
      backgroundColor: '#FFFFFF'
    }}>
      {hasHeader && (
        <div style={{
          height: '18%',
          backgroundColor: '#DBEAFE',
          borderRadius: '6px',
          border: '2px solid #93C5FD',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '11px',
          color: '#1E40AF',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Header
        </div>
      )}
      {isHero && (
        <div style={{
          height: '25%',
          backgroundColor: '#FEF3C7',
          borderRadius: '6px',
          border: '2px solid #FCD34D',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '11px',
          color: '#92400E',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Hero
        </div>
      )}
      <div style={{
        flex: 1,
        display: 'flex',
        gap: '6px',
        flexDirection: isSingleColumn ? 'column' : 'row'
      }}>
        {isSingleColumn && contentSlots.map((slot, idx) => (
          <div key={idx} style={{
            flex: 1,
            backgroundColor: '#EFF6FF',
            borderRadius: '6px',
            border: '2px solid #BFDBFE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            color: '#1E3A8A',
            fontWeight: '600',
            textAlign: 'center',
            padding: '4px'
          }}>
            {slot.name || `Slot ${idx + 1}`}
          </div>
        ))}
        {(isTwoColumn || isSidebarLeft || isSidebarRight) && contentSlots.slice(0, 2).map((slot, idx) => (
          <div key={idx} style={{
            flex: isSidebarLeft && idx === 0 ? '0 0 25%' : isSidebarRight && idx === 1 ? '0 0 25%' : 1,
            backgroundColor: '#EFF6FF',
            borderRadius: '6px',
            border: '2px solid #BFDBFE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: idx === 0 && (isSidebarLeft || isSidebarRight) ? '9px' : '11px',
            color: '#1E3A8A',
            fontWeight: '600',
            textAlign: 'center',
            padding: '4px'
          }}>
            {slot.name || (idx === 0 ? (isSidebarLeft ? 'Sidebar' : 'Left') : (isSidebarRight ? 'Sidebar' : 'Right'))}
          </div>
        ))}
        {isThreeColumn && contentSlots.slice(0, 3).map((slot, idx) => (
          <div key={idx} style={{
            flex: 1,
            backgroundColor: '#EFF6FF',
            borderRadius: '6px',
            border: '2px solid #BFDBFE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            color: '#1E3A8A',
            fontWeight: '600',
            textAlign: 'center',
            padding: '4px'
          }}>
            {slot.name || `Col ${idx + 1}`}
          </div>
        ))}
        {isFourColumn && contentSlots.slice(0, 4).map((slot, idx) => (
          <div key={idx} style={{
            flex: 1,
            backgroundColor: '#EFF6FF',
            borderRadius: '6px',
            border: '2px solid #BFDBFE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '9px',
            color: '#1E3A8A',
            fontWeight: '600',
            textAlign: 'center',
            padding: '4px'
          }}>
            {slot.name || `Col ${idx + 1}`}
          </div>
        ))}
        {!isSingleColumn && !isTwoColumn && !isThreeColumn && !isFourColumn && !isSidebarLeft && !isSidebarRight && !isHero && contentSlots.slice(0, Math.min(columnCount, 4)).map((slot, idx) => (
          <div key={idx} style={{
            flex: 1,
            backgroundColor: '#EFF6FF',
            borderRadius: '6px',
            border: '2px solid #BFDBFE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            color: '#1E3A8A',
            fontWeight: '600',
            textAlign: 'center',
            padding: '4px'
          }}>
            {slot.name || `Slot ${idx + 1}`}
          </div>
        ))}
      </div>
      {hasFooter && (
        <div style={{
          height: '15%',
          backgroundColor: '#DBEAFE',
          borderRadius: '6px',
          border: '2px solid #93C5FD',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '11px',
          color: '#1E40AF',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Footer
        </div>
      )}
    </div>
  );
};

const LayoutModal = ({ isOpen, onClose, currentLayoutId, onSelectLayout }) => {
  const [selectedLayout, setSelectedLayout] = useState(currentLayoutId);
  const [hoveredLayout, setHoveredLayout] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set());
  const layouts = getAvailableLayouts();

  if (!isOpen) return null;

  const handleSelect = () => {
    if (selectedLayout && selectedLayout !== currentLayoutId) {
      onSelectLayout(selectedLayout);
    }
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
        animation: 'fadeIn 0.2s ease'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          maxWidth: '1200px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideUp 0.3s ease'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px 32px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#111827' }}>
              Choose Page Layout
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
              Select a layout structure for your page. All layouts support multiple components per slot.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#F3F4F6',
              color: '#6B7280',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#E5E7EB';
              e.target.style.color = '#374151';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#F3F4F6';
              e.target.style.color = '#6B7280';
            }}
          >
            ✕
          </button>
        </div>

        {/* Layout Grid */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '32px'
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '20px'
            }}
          >
            {layouts.map((layout) => {
              const isSelected = selectedLayout === layout.id;
              const isCurrent = currentLayoutId === layout.id;
              const isHovered = hoveredLayout === layout.id;
              const slotCount = layout.slots?.length || 0;

              return (
                <div
                  key={layout.id}
                  onClick={() => setSelectedLayout(layout.id)}
                  onMouseEnter={() => setHoveredLayout(layout.id)}
                  onMouseLeave={() => setHoveredLayout(null)}
                  style={{
                    border: isSelected ? '3px solid #3B82F6' : '2px solid #E5E7EB',
                    borderRadius: '12px',
                    padding: '16px',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#EFF6FF' : '#FFFFFF',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
                    boxShadow: isHovered
                      ? '0 12px 24px rgba(0, 0, 0, 0.15)'
                      : '0 2px 8px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  {/* Current Layout Badge */}
                  {isCurrent && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        backgroundColor: '#10B981',
                        color: '#FFFFFF',
                        fontSize: '11px',
                        fontWeight: '700',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        zIndex: 10
                      }}
                    >
                      CURRENT
                    </div>
                  )}

                  {/* Layout Preview Image */}
                  <div
                    style={{
                      width: '100%',
                      height: '180px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      marginBottom: '12px',
                      backgroundColor: '#F9FAFB',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #E5E7EB'
                    }}
                  >
                    {imageErrors.has(layout.id) ? (
                      <LayoutPreviewVisual layout={layout} />
                    ) : (
                      <img
                        src={layout.preview}
                        alt={layout.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          backgroundColor: '#FFFFFF'
                        }}
                        onError={(e) => {
                          console.warn(`Failed to load layout image: ${layout.preview}`, e);
                          e.target.style.display = 'none';
                          setImageErrors(prev => new Set([...prev, layout.id]));
                        }}
                        onLoad={(e) => {
                          // Image loaded successfully, ensure it's visible
                          e.target.style.display = 'block';
                          console.log(`Successfully loaded layout image: ${layout.preview}`);
                        }}
                      />
                    )}
                  </div>

                  {/* Layout Info */}
                  <div>
                    <h3
                      style={{
                        margin: '0 0 6px 0',
                        fontSize: '16px',
                        fontWeight: '700',
                        color: isSelected ? '#1E40AF' : '#111827'
                      }}
                    >
                      {layout.name}
                    </h3>
                    <p
                      style={{
                        margin: '0 0 12px 0',
                        fontSize: '13px',
                        color: '#6B7280',
                        lineHeight: '1.5'
                      }}
                    >
                      {layout.description}
                    </p>

                    {/* Slot Count Badge */}
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: isSelected ? '#DBEAFE' : '#F3F4F6',
                        color: isSelected ? '#1E40AF' : '#6B7280',
                        fontSize: '12px',
                        fontWeight: '600',
                        padding: '4px 10px',
                        borderRadius: '6px'
                      }}
                    >
                      <span>📦</span>
                      <span>{slotCount} Slots</span>
                    </div>
                  </div>

                  {/* Selection Indicator */}
                  {isSelected && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '12px',
                        left: '12px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: '#3B82F6',
                        color: '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '700'
                      }}
                    >
                      ✓
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer Actions */}
        <div
          style={{
            padding: '20px 32px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <div style={{ fontSize: '13px', color: '#6B7280' }}>
            {selectedLayout !== currentLayoutId && selectedLayout && (
              <span>⚠️ Changing layout will require you to reorganize your content</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid #D1D5DB',
                backgroundColor: '#FFFFFF',
                color: '#374151',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#F9FAFB';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#FFFFFF';
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedLayout || selectedLayout === currentLayoutId}
              style={{
                padding: '10px 24px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: selectedLayout && selectedLayout !== currentLayoutId ? '#3B82F6' : '#D1D5DB',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: '600',
                cursor: selectedLayout && selectedLayout !== currentLayoutId ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                opacity: selectedLayout && selectedLayout !== currentLayoutId ? 1 : 0.6
              }}
              onMouseEnter={(e) => {
                if (selectedLayout && selectedLayout !== currentLayoutId) {
                  e.target.style.backgroundColor = '#2563EB';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedLayout && selectedLayout !== currentLayoutId) {
                  e.target.style.backgroundColor = '#3B82F6';
                }
              }}
            >
              {selectedLayout === currentLayoutId ? 'Already Selected' : 'Apply Layout'}
            </button>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { 
              opacity: 0;
              transform: translateY(20px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

export default LayoutModal;

















